import { ConfirmationDialog } from '@components/ConfirmationDialog';
import { ErrorHandlingModal } from '@components/ErrorHandlingModal';
import { FilesSelector } from '@components/FilesSelector/FilesSelector';
import { useReadFilesAsBase64 } from '@components/FilesSelector/hooks/useReadFilesAsBase64';
import { ControlledSelectField, ControlledTextField } from '@components/Form';
import { ModalDialog } from '@components/ModalDialog';
import { UploadStatus } from '@components/UploadStatus';
import { useMethodsConfirmationDialog } from '@hooks/useMethodsConfirmationDialog';
import { cnMixSpace } from '@uikit';
import { RequestStatus } from 'features/request/requestSlice';

import cn from 'classnames';
import { useController, useForm } from 'react-hook-form';

import { useUerBaseCostMassUpload } from './hooks/useUerBaseCostMassUpload';
import {
    MassUploadType,
    chapterOptions,
    massUploadDefaultValues,
    massUploadModalSettings,
    uerClassifierOptions,
} from './models/constants';
import type { MassUploadFormValues, SelectOption } from './models/types';

import Style from './catalogUerBaseCostMassUpload.module.css';

type CatalogUerBaseCostMassUploadModalProps = {
    uploadType: MassUploadType;
    onClose: () => void;
    catalogId: number;
    onSuccessAction: () => void;
};

const getItemKey = (item: SelectOption) => item.id;

const invalidFileTypeLocale = {
    'file-invalid-type': (props: { file?: File }) =>
        (
            <span>
                {props?.file?.name} <br /> Неверный формат файла
            </span>
        ) as unknown as string,
};

export const CatalogUerBaseCostMassUploadModal = ({
    uploadType,
    onClose,
    catalogId,
    onSuccessAction,
}: CatalogUerBaseCostMassUploadModalProps) => {
    const { title, accept, multiple, withSelectors } = massUploadModalSettings[uploadType];

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => onClose(),
        });

    const {
        control,
        handleSubmit,
        formState: { isDirty },
    } = useForm<MassUploadFormValues>({
        defaultValues: massUploadDefaultValues,
    });

    const { field: filesField } = useController({
        name: 'files',
        control,
    });

    const files = filesField.value;
    const base64Files = useReadFilesAsBase64(files);

    const {
        makeRequest,
        status,
        isSubmitted,
        data,
        dataResult,
        dataHasValidation,
        errors,
        resetUploadData,
        handleCreateReport,
    } = useUerBaseCostMassUpload({
        uploadType,
        catalogId,
        onSuccessAction,
    });

    const isFetching = status === RequestStatus.FETCHING;

    const onFilesChange = (newFiles?: File[], replaceAll?: boolean) => {
        if (replaceAll || !multiple) {
            filesField.onChange(newFiles ?? []);

            return;
        }

        filesField.onChange([...(files ?? []), ...(newFiles ?? [])]);
    };

    const onCloseModal = () => {
        if (isDirty) {
            handleOpenConfirmationDialog();

            return;
        }

        onClose();
    };

    const onConfirm = handleSubmit(({ uerClassifier, chapter, justification }) => {
        makeRequest({ files: base64Files, uerClassifier, chapter, justification });
    });

    const getStatusText = () => {
        if (uploadType === MassUploadType.EstimateBasisFiles) {
            return 'Загружены все файлы.';
        }

        if (uploadType === MassUploadType.WorkScopes) {
            return 'Виды работ для расценок загружены.';
        }

        return 'Загрузка файла выполнена успешно.';
    };

    const handleCloseModalUploadStatus = () => {
        resetUploadData();
        onClose();
        onSuccessAction();
    };

    return (
        <ModalDialog
            title={title}
            onConfirm={onConfirm}
            onClose={onCloseModal}
            btnConfirmDisabled={!files?.length}
            isCommit={isFetching}
            className={Style.modalStyle}
            btnConfirmLabel="Добавить"
            withTitleCloseButton
            bodyMinHeight="140px"
            isOnClickOutsideDisable
            isOpen
        >
            <div className={Style.bodyStyle}>
                {withSelectors && (
                    <div className={cn(cnMixSpace({ mB: 'm' }), Style.inputsWrapper)}>
                        <div className={Style.wrapperSelect}>
                            <div className={Style.selectItem}>
                                <ControlledSelectField
                                    name="uerClassifier"
                                    label="Тип УЕР"
                                    control={control}
                                    items={uerClassifierOptions}
                                    getItemKey={getItemKey}
                                    size="s"
                                    labelPosition="top"
                                    placeholder="Выберите"
                                />
                            </div>
                            <div className={Style.selectItem}>
                                <ControlledSelectField
                                    name="chapter"
                                    label="Глава в расчете"
                                    control={control}
                                    items={chapterOptions}
                                    getItemKey={getItemKey}
                                    size="s"
                                    labelPosition="top"
                                    placeholder="Выберите"
                                />
                            </div>
                        </div>

                        <ControlledTextField
                            name="justification"
                            label="Обоснование"
                            control={control}
                            labelPosition="top"
                            required
                            rules={{
                                required: 'Поле обязательно для заполнения',
                                maxLength: {
                                    value: 1024,
                                    message: 'не более 1024 символов',
                                },
                            }}
                        />
                    </div>
                )}

                <div className={Style.filesSelector}>
                    <FilesSelector
                        files={files}
                        accept={accept}
                        typeName={accept[0]}
                        manualOperationName="выберите вручную"
                        onFilesChange={onFilesChange}
                        isExternalFilesState
                        multiple={multiple}
                        isLoading={isFetching}
                        locale={invalidFileTypeLocale}
                        withoutEndingOfWordFile={!multiple}
                    />
                </div>
            </div>

            {status === RequestStatus.SUCCESS && dataResult && !dataHasValidation && (
                <UploadStatus
                    isOpened
                    handleCloseModal={handleCloseModalUploadStatus}
                    statusText={getStatusText()}
                    titleText={title}
                    status="success"
                    btnText="Понятно"
                />
            )}

            <ErrorHandlingModal
                isSuccessProcessed={!!dataResult}
                titleText={title}
                isSubmitted={isSubmitted}
                statusRequest={status}
                dataRequest={data}
                errorsRequest={errors}
                handleCloseModalError={handleCloseModalUploadStatus}
                customButtonText="Понятно"
                handleCreateReport={handleCreateReport}
            />

            <ConfirmationDialog
                textHeader="Отмена действий"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

CatalogUerBaseCostMassUploadModal.displayName = 'CatalogUerBaseCostMassUploadModal';
